/**
 * Print Utility for Attendance Reports
 * Provides enhanced printing functionality for attendance data
 */

class PrintUtility {
    /**
     * Print attendance report with custom formatting
     * @param {Object} config - Print configuration
     * @param {Array} attendanceData - Array of attendance records
     * @param {Object} filters - Current filter settings
     * @param {Object} summary - Summary statistics
     * @param {Array} dateRange - Date range array
     */
    static printAttendanceReport(config = {}) {
        const {
            attendanceData = [],
            filters = {},
            summary = {},
            dateRange = [],
            holidays = []
        } = config;

        if (attendanceData.length === 0) {
            alert('No data to print');
            return;
        }

        // Create print window
        const printWindow = window.open('', '_blank', 'width=1200,height=800');

        // Build print HTML
        const printHTML = this.generatePrintHTML(attendanceData, filters, summary, dateRange, holidays);

        printWindow.document.write(printHTML);
        printWindow.document.close();

        // Print after content loads
        printWindow.onload = () => {
            printWindow.focus();
            setTimeout(() => {
                printWindow.print();
                // Optional: Close window after printing
                // printWindow.close();
            }, 500);
        };
    }

    /**
     * Generate HTML for print
     */
    static generatePrintHTML(attendanceData, filters, summary, dateRange, holidays) {
        const today = new Date().toLocaleDateString();
        const time = new Date().toLocaleTimeString();

        return `
            <!DOCTYPE html>
            <html lang="en">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Attendance Report - ${today}</title>
                <style>
                    @page {
                        size: landscape;
                        margin: 0.5cm;
                    }
                    
                    body {
                        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                        font-size: 12px;
                        color: #333;
                        margin: 0;
                        padding: 10px;
                    }
                    
                    .print-header {
                        text-align: center;
                        border-bottom: 2px solid #000;
                        padding-bottom: 10px;
                        margin-bottom: 20px;
                    }
                    
                    .print-header h1 {
                        font-size: 24px;
                        margin: 0;
                        color: #2c3e50;
                    }
                    
                    .print-header .subtitle {
                        font-size: 14px;
                        color: #7f8c8d;
                        margin: 5px 0;
                    }
                    
                    .print-meta {
                        display: flex;
                        justify-content: space-between;
                        margin-bottom: 15px;
                        padding: 10px;
                        background-color: #f8f9fa;
                        border: 1px solid #dee2e6;
                    }
                    
                    .summary-grid {
                        display: grid;
                        grid-template-columns: repeat(6, 1fr);
                        gap: 10px;
                        margin-bottom: 20px;
                    }
                    
                    .summary-item {
                        text-align: center;
                        padding: 10px;
                        border: 1px solid #dee2e6;
                        border-radius: 4px;
                        background-color: #fff;
                    }
                    
                    .summary-value {
                        font-size: 18px;
                        font-weight: bold;
                        margin: 5px 0;
                    }
                    
                    .summary-label {
                        font-size: 11px;
                        color: #6c757d;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                    }
                    
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-top: 10px;
                    }
                    
                    th {
                        background-color: #f8f9fa;
                        color: #495057;
                        font-weight: bold;
                        text-align: center;
                        padding: 8px 4px;
                        border: 1px solid #dee2e6;
                        font-size: 11px;
                        position: sticky;
                        top: 0;
                        z-index: 10;
                    }
                    
                    td {
                        padding: 6px 4px;
                        border: 1px solid #dee2e6;
                        text-align: center;
                        font-size: 11px;
                        vertical-align: middle;
                    }
                    
                    .employee-name {
                        text-align: left;
                        font-weight: bold;
                    }
                    
                    .badge {
                        display: inline-block;
                        padding: 2px 6px;
                        border-radius: 3px;
                        font-size: 10px;
                        font-weight: bold;
                        min-width: 20px;
                    }
                    
                    .badge-success { background-color: #d1e7dd; color: #0f5132; }
                    .badge-danger { background-color: #f8d7da; color: #842029; }
                    .badge-warning { background-color: #fff3cd; color: #664d03; }
                    .badge-info { background-color: #cff4fc; color: #055160; }
                    .badge-primary { background-color: #cfe2ff; color: #052c65; }
                    .badge-secondary { background-color: #e2e3e5; color: #2b2f32; }
                    .badge-purple { background-color: #e2d9f3; color: #3d2c5e; }
                    .badge-dark { background-color: #d3d3d4; color: #141619; }
                    
                    .holiday-cell {
                        background-color: #f3e5f5 !important;
                    }
                    
                    .weeklyoff-cell {
                        background-color: #f8f9fa !important;
                    }
                    
                    .legend {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 15px;
                        margin: 15px 0;
                        padding: 10px;
                        border: 1px solid #dee2e6;
                        background-color: #f8f9fa;
                    }
                    
                    .legend-item {
                        display: flex;
                        align-items: center;
                        gap: 5px;
                    }
                    
                    .footer {
                        margin-top: 20px;
                        padding-top: 10px;
                        border-top: 1px solid #dee2e6;
                        text-align: center;
                        font-size: 10px;
                        color: #6c757d;
                    }
                    
                    .print-date {
                        text-align: right;
                        margin-bottom: 10px;
                        font-size: 11px;
                        color: #6c757d;
                    }
                    
                    .company-header {
                        margin-bottom: 20px;
                        text-align: center;
                    }
                    
                    .company-name {
                        font-size: 20px;
                        font-weight: bold;
                        color: #2c3e50;
                    }
                    
                    .company-address {
                        font-size: 12px;
                        color: #7f8c8d;
                    }
                    
                    @media print {
                        .page-break {
                            page-break-before: always;
                        }
                        
                        .no-print {
                            display: none !important;
                        }
                    }
                </style>
            </head>
            <body>
                <div class="company-header">
                    <div class="company-name">COMPANY NAME</div>
                    <div class="company-address">Company Address, City, Country</div>
                </div>
                
                <div class="print-header">
                    <h1>ATTENDANCE REPORT</h1>
                    <div class="subtitle">Detailed Attendance Records</div>
                </div>
                
                <div class="print-meta">
                    <div>
                        <strong>Period:</strong> ${filters.from_date || ''} to ${filters.to_date || ''}
                    </div>
                    <div>
                        <strong>Generated:</strong> ${today} at ${time}
                    </div>
                    <div>
                        <strong>Total Employees:</strong> ${attendanceData.length}
                    </div>
                </div>
                
                <div class="summary-grid">
                    <div class="summary-item">
                        <div class="summary-value" style="color: #495057;">${summary.total_ed || 0}</div>
                        <div class="summary-label">Total ED</div>
                    </div>
                    <div class="summary-item">
                        <div class="summary-value" style="color: #198754;">${summary.total_wd || 0}</div>
                        <div class="summary-label">Total WD</div>
                    </div>
                    <div class="summary-item">
                        <div class="summary-value" style="color: #0dcaf0;">${summary.total_fh || 0}</div>
                        <div class="summary-label">Total FH</div>
                    </div>
                    <div class="summary-item">
                        <div class="summary-value" style="color: #ffc107;">${summary.total_loff || 0}</div>
                        <div class="summary-label">Total LOff</div>
                    </div>
                    <div class="summary-item">
                        <div class="summary-value" style="color: #dc3545;">${summary.total_lop || 0}</div>
                        <div class="summary-label">Total LOP</div>
                    </div>
                    <div class="summary-item">
                        <div class="summary-value" style="color: #6c757d;">${summary.total_employees || 0}</div>
                        <div class="summary-label">Employees</div>
                    </div>
                </div>
                
                <div class="legend">
                    <div class="legend-item"><span class="badge badge-success">P</span> Present</div>
                    <div class="legend-item"><span class="badge badge-danger">A</span> Absent</div>
                    <div class="legend-item"><span class="badge badge-info">CL</span> Casual Leave</div>
                    <div class="legend-item"><span class="badge badge-primary">EL</span> Earned Leave</div>
                    <div class="legend-item"><span class="badge badge-warning">PA</span> Half Day</div>
                    <div class="legend-item"><span class="badge badge-purple">H</span> Holiday</div>
                    <div class="legend-item"><span class="badge badge-dark">LO</span> Layoff</div>
                    <div class="legend-item"><span class="badge badge-secondary">WO</span> Weekly Off</div>
                </div>
                
                <table>
                    <thead>
                        <tr>
                            <th rowspan="2">S.No</th>
                            <th rowspan="2">Unit</th>
                            <th rowspan="2">Employee</th>
                            <th rowspan="2">Dept</th>
                            <th rowspan="2">Designation</th>
                            <th rowspan="2">ED</th>
                            <th rowspan="2">WD</th>
                            <th rowspan="2">FH</th>
                            <th rowspan="2">LOff</th>
                            <th rowspan="2">LOP</th>
                            ${dateRange.map(date => {
            const holiday = holidays.find(h => h.hdate === date);
            const day = new Date(date);
            return `
                                    <th colspan="1" class="${holiday ? 'holiday-cell' : ''}">
                                        <div>${day.getDate()}</div>
                                        <div style="font-size: 9px; color: #6c757d;">${day.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                                    </th>
                                `;
        }).join('')}
                        </tr>
                        <tr>
                            ${dateRange.map(date => {
            const holiday = holidays.find(h => h.hdate === date);
            return `<th class="${holiday ? 'holiday-cell' : ''}">Status</th>`;
        }).join('')}
                        </tr>
                    </thead>
                    <tbody>
                        ${attendanceData.map((emp, index) => `
                            <tr>
                                <td>${index + 1}</td>
                                <td>${emp.unit_name || ''}</td>
                                <td class="employee-name">${emp.emp_name || ''}<br><small style="color: #6c757d;">(${emp.emp_code || ''})</small></td>
                                <td>${emp.dept_name || ''}</td>
                                <td>${emp.desig_name || ''}</td>
                                <td><strong>${emp.ed || 0}</strong></td>
                                <td style="color: #198754;"><strong>${emp.wd || 0}</strong></td>
                                <td style="color: #0dcaf0;"><strong>${emp.fh || 0}</strong></td>
                                <td style="color: #ffc107;"><strong>${emp.loff || 0}</strong></td>
                                <td style="color: #dc3545;"><strong>${emp.lop || 0}</strong></td>
                                ${dateRange.map(date => {
            const dayData = emp.daily_data?.[date] || {};
            let badgeClass = 'badge-secondary';
            let badgeText = '-';

            if (dayData.is_holiday) {
                badgeClass = 'badge-purple';
                badgeText = 'H';
            } else if (dayData.is_layoff) {
                badgeClass = 'badge-dark';
                badgeText = 'LO';
            } else if (dayData.is_weeklyoff) {
                badgeClass = 'badge-secondary';
                badgeText = 'WO';
            } else if (dayData.status_code) {
                switch (dayData.status_code) {
                    case 'P': badgeClass = 'badge-success'; break;
                    case 'A': badgeClass = 'badge-danger'; break;
                    case 'CL': badgeClass = 'badge-info'; break;
                    case 'EL': badgeClass = 'badge-primary'; break;
                    case 'PA': badgeClass = 'badge-warning'; break;
                    default: badgeClass = 'badge-secondary';
                }
                badgeText = dayData.status_code;
            }

            const cellClass = dayData.is_holiday ? 'holiday-cell' :
                dayData.is_weeklyoff ? 'weeklyoff-cell' : '';

            return `
                                        <td class="${cellClass}">
                                            <span class="badge ${badgeClass}">${badgeText}</span>
                                        </td>
                                    `;
        }).join('')}
                            </tr>
                        `).join('')}
                        
                        ${summary.daily_totals ? `
                            <tr style="background-color: #f8f9fa; font-weight: bold;">
                                <td colspan="5" style="text-align: right;">GRAND TOTAL:</td>
                                <td>${summary.total_ed || 0}</td>
                                <td style="color: #198754;">${summary.total_wd || 0}</td>
                                <td style="color: #0dcaf0;">${summary.total_fh || 0}</td>
                                <td style="color: #ffc107;">${summary.total_loff || 0}</td>
                                <td style="color: #dc3545;">${summary.total_lop || 0}</td>
                                ${dateRange.map(date => {
            const dailyTotal = summary.daily_totals?.find(d => d.date === date);
            return `<td>${dailyTotal?.present_count || 0}</td>`;
        }).join('')}
                            </tr>
                        ` : ''}
                    </tbody>
                </table>
                
                <div class="footer">
                    <div>This is a computer-generated report. No signature required.</div>
                    <div>Page 1 of 1 | Confidential</div>
                    <div>Printed from Attendance Management System</div>
                </div>
                
                <div class="print-date">
                    Printed on: ${today} ${time}
                </div>
            </body>
            </html>
        `;
    }

    /**
     * Print specific employee details
     */
    static printEmployeeDetails(employee, dateRange, filters) {
        const printWindow = window.open('', '_blank', 'width=800,height=600');
        const today = new Date().toLocaleDateString();

        const html = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>${employee.emp_name} - Attendance Details</title>
                <style>
                    body { font-family: Arial, sans-serif; margin: 20px; }
                    .header { text-align: center; margin-bottom: 20px; }
                    .employee-info { margin-bottom: 20px; }
                    .attendance-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 5px; }
                    .day-cell { border: 1px solid #ccc; padding: 5px; text-align: center; }
                    .present { background-color: #d4edda; }
                    .absent { background-color: #f8d7da; }
                    .holiday { background-color: #e2d9f3; }
                </style>
            </head>
            <body>
                <div class="header">
                    <h2>Employee Attendance Details</h2>
                    <p>Period: ${filters.from_date} to ${filters.to_date}</p>
                </div>
                <div class="employee-info">
                    <p><strong>Name:</strong> ${employee.emp_name}</p>
                    <p><strong>Code:</strong> ${employee.emp_code}</p>
                    <p><strong>Department:</strong> ${employee.dept_name}</p>
                    <p><strong>Designation:</strong> ${employee.desig_name}</p>
                </div>
                <div class="attendance-grid">
                    ${dateRange.map(date => {
            const dayData = employee.daily_data?.[date] || {};
            let cellClass = '';
            let status = '-';

            if (dayData.is_holiday) {
                cellClass = 'holiday';
                status = 'H';
            } else if (dayData.status_code === 'P') {
                cellClass = 'present';
                status = 'P';
            } else if (dayData.status_code === 'A') {
                cellClass = 'absent';
                status = 'A';
            }

            return `
                            <div class="day-cell ${cellClass}">
                                <div>${new Date(date).getDate()}</div>
                                <div>${status}</div>
                            </div>
                        `;
        }).join('')}
                </div>
                <div style="margin-top: 20px; text-align: center; color: #666;">
                    Printed on: ${today}
                </div>
            </body>
            </html>
        `;

        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.print();
    }
}

export default PrintUtility;